import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  LayoutDashboard, FileQuestion, ClipboardList, BarChart3, 
  Shield, LogOut, Users, Award, Clock, TrendingUp,
  ChevronRight, Database, Activity, MessageSquare
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, AreaChart, Area } from 'recharts';

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [analytics, setAnalytics] = useState(null);
  const [topCandidates, setTopCandidates] = useState([]);
  const [topicPerf, setTopicPerf] = useState([]);
  const [scoreDist, setScoreDist] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [analyticsRes, topRes, topicRes, scoreRes, fbRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/top-candidates?limit=5`, { headers }),
        axios.get(`${API}/admin/analytics/topic-performance`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/analytics/score-distribution`, { headers }).catch(() => ({ data: { distribution: [] } })),
        axios.get(`${API}/admin/feedback/stats`, { headers }).catch(() => ({ data: { exam_stats: [], total_feedback: 0 } }))
      ]);
      setAnalytics(analyticsRes.data);
      setTopCandidates(topRes.data);
      setTopicPerf(topicRes.data);
      setScoreDist(scoreRes.data.distribution || []);
      setFeedbackStats(fbRes.data);
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  const seedQuestions = async () => {
    try {
      const response = await axios.post(`${API}/admin/seed-questions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to seed questions");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/auth");
    toast.success("Logged out successfully");
  };

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin", active: location.pathname === "/admin" },
    { icon: FileQuestion, label: "Question Bank", path: "/admin/questions" },
    { icon: ClipboardList, label: "Exams", path: "/admin/exams" },
    { icon: BarChart3, label: "Results", path: "/admin/results" },
    { icon: Users, label: "Organisers", path: "/admin/organisers" },
    { icon: MessageSquare, label: "Feedback", path: "/admin/feedback" },
    { icon: Activity, label: "IP Logs", path: "/admin/logs" }
  ];

  const COLORS = ['#4f46e5', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  const DIST_COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#4f46e5'];

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <aside className="sidebar">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>edSolutions360</span>
              <p className="text-xs text-slate-500">Examination Portal</p>
            </div>
          </div>
          <nav className="space-y-2">
            {navItems.map((item) => (
              <div key={item.path} className={`nav-item ${item.active ? 'active' : ''}`} onClick={() => navigate(item.path)} data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}>
                <item.icon className="w-5 h-5" /><span>{item.label}</span>
              </div>
            ))}
          </nav>
        </div>
        <div className="mt-auto p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><Users className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>
      </aside>

      <main className="main-content p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Dashboard</h1>
              <p className="text-slate-600 mt-1">Welcome back, {user?.full_name?.split(' ')[0]}</p>
            </div>
            <Button onClick={seedQuestions} className="bg-indigo-600 hover:bg-indigo-700" data-testid="seed-questions-btn">
              <Database className="w-4 h-4 mr-2" />Seed Questions
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8 stagger-children">
                <Card className="stats-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Questions</p>
                      <p className="text-3xl font-bold text-slate-900">{analytics?.total_questions || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center">
                      <FileQuestion className="w-6 h-6 text-indigo-600" />
                    </div>
                  </div>
                </Card>
                <Card className="stats-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Exams</p>
                      <p className="text-3xl font-bold text-slate-900">{analytics?.total_exams || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                      <ClipboardList className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </Card>
                <Card className="stats-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Candidates</p>
                      <p className="text-3xl font-bold text-slate-900">{analytics?.total_candidates || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <Users className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </Card>
                <Card className="stats-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Completed</p>
                      <p className="text-3xl font-bold text-slate-900">{analytics?.completed_attempts || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                      <Award className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </Card>
                <Card className="stats-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Feedback</p>
                      <p className="text-3xl font-bold text-slate-900">{feedbackStats?.total_feedback || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </Card>
              </div>

              {/* Charts Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Score Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BarChart3 className="w-5 h-5 text-indigo-600" />Score Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {scoreDist.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={scoreDist}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                          <Tooltip formatter={(v) => [`${v} candidates`, 'Count']} />
                          <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                            {scoreDist.map((_, i) => (
                              <Cell key={i} fill={DIST_COLORS[i % DIST_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[280px] flex items-center justify-center text-slate-500">No data yet</div>
                    )}
                  </CardContent>
                </Card>

                {/* Question Distribution Pie */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <TrendingUp className="w-5 h-5 text-indigo-600" />Question Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics?.question_stats?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={analytics.question_stats.map(s => ({ name: `${s._id.role} ${s._id.difficulty}`, value: s.count }))}
                            cx="50%" cy="50%" outerRadius={90} innerRadius={50} fill="#8884d8" dataKey="value" label={({ name, value }) => `${name}: ${value}`}
                          >
                            {analytics.question_stats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[280px] flex items-center justify-center text-slate-500">No questions</div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row 2 - Topic Performance */}
              {topicPerf.length > 0 && (
                <Card className="mb-8">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Activity className="w-5 h-5 text-indigo-600" />Topic Performance Heatmap
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {topicPerf.slice(0, 18).map((t, i) => {
                        const intensity = Math.min(100, t.accuracy);
                        const bg = intensity >= 70 ? 'bg-emerald-50 border-emerald-200' :
                                   intensity >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
                        const text = intensity >= 70 ? 'text-emerald-700' :
                                     intensity >= 50 ? 'text-amber-700' : 'text-red-700';
                        return (
                          <div key={i} className={`p-3 rounded-xl border ${bg} transition-transform hover:scale-105`} data-testid={`topic-tile-${i}`}>
                            <p className="text-xs text-slate-500 truncate mb-1">{t.topic}</p>
                            <p className={`text-xl font-bold ${text}`}>{t.accuracy}%</p>
                            <p className="text-xs text-slate-400">{t.total_attempts} attempts</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top Candidates */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Award className="w-5 h-5 text-indigo-600" />Top Candidates
                  </CardTitle>
                  <Button variant="ghost" onClick={() => navigate("/admin/results")} data-testid="view-all-results-btn">
                    View All<ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {topCandidates.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr><th>Rank</th><th>Candidate</th><th>Exam</th><th>Score</th><th>Accuracy</th><th>Hard Q Accuracy</th><th>Time</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                          {topCandidates.map((c) => {
                            const hardData = c.difficulty_breakdown?.hard || { total: 0, correct: 0 };
                            const hardAcc = hardData.total > 0 ? Math.round((hardData.correct / hardData.total) * 100) : 0;
                            return (
                              <tr key={c.id}>
                                <td>
                                  <div className={`rank-badge ${c.rank === 1 ? 'rank-1' : c.rank === 2 ? 'rank-2' : c.rank === 3 ? 'rank-3' : 'rank-other'}`}>#{c.rank}</div>
                                </td>
                                <td>
                                  <div><p className="font-medium">{c.candidate_name}</p><p className="text-sm text-slate-500">{c.candidate_email}</p></div>
                                </td>
                                <td>{c.exam_name}</td>
                                <td>
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold">{c.score}</span>
                                    <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(100, c.accuracy)}%` }} />
                                    </div>
                                  </div>
                                </td>
                                <td><span className={`font-medium ${c.accuracy >= 80 ? 'text-emerald-600' : c.accuracy >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{c.accuracy}%</span></td>
                                <td><span className={`font-medium ${hardAcc >= 70 ? 'text-emerald-600' : hardAcc >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{hardAcc}%</span></td>
                                <td>{Math.floor(c.time_taken_seconds / 60)}m {c.time_taken_seconds % 60}s</td>
                                <td><span className="recommendation-badge">{c.recommendation}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">No completed exams yet</div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
